import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Loader2, PenTool, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { createBlog } from "../../api/blog";

const blogSchema = z.object({
 title: z.string().min(5, "Title must be at least 5 characters"),
 content: z.string().min(20, "Content must be at least 20 characters"),
 coverImage: z.string().url("Invalid URL").optional().or(z.literal("")),
});

export default function CreateBlog() {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);

 const {
 register,
 handleSubmit,
 formState: { errors },
 } = useForm({
 resolver: zodResolver(blogSchema),
 });

 const onSubmit = async (data) => {
 setLoading(true);
 try {
 await createBlog(data);
 toast.success("Blog post created successfully!");
 navigate("/blog");
 } catch (err) {
 console.error(err);
 toast.error(err.response?.data?.message || "Failed to create blog post");
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg">
 <PenTool className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Write a Blog Post</h1>
 </div>
 <p className="text-muted-foreground">Share insights and updates with the community</p>
 </div>

 <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
 <Card>
 <CardHeader>
 <CardTitle>Post Details</CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="title">Title</Label>
 <Input 
 id="title"
 {...register("title")} 
 placeholder="e.g. The Future of Remote Work" 
 className="text-lg font-medium" 
 />
 {errors.title && (
 <p className="text-sm text-red-500">{errors.title.message}</p>
 )}
 </div>

 <div className="space-y-2">
 <Label htmlFor="coverImage">Cover Image URL (Optional)</Label>
 <Input 
 id="coverImage"
 {...register("coverImage")} 
 placeholder="https://..." 
 />
 {errors.coverImage && (
 <p className="text-sm text-red-500">{errors.coverImage.message}</p>
 )}
 </div>

 <div className="space-y-2">
 <Label htmlFor="content">Content</Label>
 <Textarea
 id="content"
 {...register("content")}
 className="min-h-[400px] text-base"
 placeholder="Write your article here..."
 />
 {errors.content && (
 <p className="text-sm text-red-500">{errors.content.message}</p>
 )}
 </div>
 </CardContent>
 </Card>

 <div className="flex flex-col sm:flex-row justify-end gap-3">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate("/blog")}
 disabled={loading}
 >
 Cancel
 </Button>
 <Button type="submit" disabled={loading} className="min-w-[140px]">
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 animate-spin" />
 Publishing...
 </>
 ) : (
 <>
 <PenTool className="mr-2 h-4 w-4" />
 Publish Post
 </>
 )}
 </Button>
 </div>
 </form>
 </div>
 </div>
 );
}
